import React, { useMemo, useState, useEffect } from 'react'
import {
	Box,
	Checkbox,
	Chip,
	Collapse,
	Dialog,
	FormControlLabel,
	IconButton,
	Menu,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type { ScheduleTasksModalData } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

type TaskRow = {
	id: string
	displayId: string
	startDate: string
	endDate: string
	impedeEscalar: 'Sim' | 'Nao'
	estado: 'Nao Concluida' | 'Concluida'
	descricao: string
	nome: string
	nr: string
	nrMec: string
	nrFrota: string
	matricula: string
}

type CreateTaskForm = {
	startDate: string
	endDate: string
	destinatario: string
	motorista: string
	viatura: string
	descricao: string
	recorrencia: string
	impedeEscalar: boolean
}

type ScheduleTaskItem = ScheduleTasksModalData['tasks'][number]
type PillMenuContext = {
	rowId: string
	field: 'impedeEscalar' | 'estado'
}

const defaultScheduleTasks: ScheduleTaskItem[] = [
	{
		id: '4203-1',
		code: '4203',
		title: 'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
		description: 'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
		status: 'pending',
		startDate: '02/05/2024 - 08:00',
		endDate: '03/05/2024 - 08:00',
		impedeEscalar: false,
		estado: 'Nao Concluida',
		descricao: 'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
		nome: 'Gabriel Santos',
		nr: '0137',
		nrM: '0137',
	},
	{
		id: '4203-2',
		code: '4203',
		title: 'Ausente das 15h as 16h.',
		description: 'Ausente das 15h as 16h.',
		status: 'completed',
		startDate: '02/05/2024 - 08:00',
		endDate: '03/05/2024 - 08:00',
		impedeEscalar: true,
		estado: 'Concluida',
		descricao: 'Ausente das 15h as 16h.',
		nome: 'Rita Sofia',
		nr: '0137',
		nrM: '0137',
	},
	{
		id: '4202',
		code: '4202',
		title: 'Precisa tirar o dia todo.',
		description: 'Precisa tirar o dia todo.',
		status: 'pending',
		startDate: '02/05/2024 - 08:00',
		endDate: '03/05/2024 - 08:00',
		impedeEscalar: true,
		estado: 'Nao Concluida',
		descricao: 'Precisa tirar o dia todo.',
		nome: 'Jorge Rodrigues',
		nr: '0137',
		nrM: '0137',
	},
	{
		id: '4201',
		code: '4201',
		title: 'Consulta durante a manha.',
		description: 'Consulta durante a manha.',
		status: 'pending',
		startDate: '02/05/2024 - 08:00',
		endDate: '03/05/2024 - 08:00',
		impedeEscalar: false,
		estado: 'Nao Concluida',
		descricao: 'Consulta durante a manha.',
		nome: 'Carla Silva',
		nr: '0137',
		nrM: '0137',
	},
	{
		id: '4200',
		code: '4200',
		title: 'Precisa tirar a tarde.',
		description: 'Precisa tirar a tarde.',
		status: 'completed',
		startDate: '01/05/2024 - 08:00',
		endDate: '02/05/2024 - 08:00',
		impedeEscalar: false,
		estado: 'Concluida',
		descricao: 'Precisa tirar a tarde.',
		nome: 'Joao Gomes',
		nr: '0137',
		nrM: '0137',
	},
]

const tableColumns = [
	{ key: 'displayId', label: 'ID', width: '84px' },
	{ key: 'startDate', label: 'Data Inicio', width: '120px' },
	{ key: 'endDate', label: 'Data Fim', width: '120px' },
	{ key: 'impedeEscalar', label: 'Impede Escalar?', width: '108px' },
	{ key: 'estado', label: 'Estado', width: '158px' },
	{ key: 'descricao', label: 'Descricao', width: '220px' },
	{ key: 'nome', label: 'Nome', width: '150px' },
	{ key: 'nr', label: 'Nr', width: '31px' },
	{ key: 'nrMec', label: 'Nr Mec', width: '47px' },
	{ key: 'nrFrota', label: 'Nr Frota', width: '54px' },
	{ key: 'matricula', label: 'Matricula', width: '64px' },
]

const outlinedFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		height: '40px',
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		fontSize: '13px',
		fontWeight: 500,
		fontFamily: 'Poppins, sans-serif',
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'& .MuiSelect-icon': {
			color: '#00A5FF',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '10px 12px',
	},
}

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row.Mui-selected': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
	},
	'& .MuiDataGrid-row.Mui-selected:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.12)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const getImpedeColors = (value: TaskRow['impedeEscalar']) => {
	if (value === 'Sim') {
		return {
			background: 'rgba(255, 92, 0, 0.05)',
			border: '#FF5C00',
			text: '#FF5C00',
		}
	}
	return {
		background: 'rgba(0, 165, 255, 0.05)',
		border: '#00A5FF',
		text: '#00A5FF',
	}
}

const getEstadoColors = (value: TaskRow['estado']) => {
	if (value === 'Concluida') {
		return {
			background: 'rgba(19, 165, 116, 0.05)',
			border: '#13A574',
			text: '#13A574',
		}
	}
	return {
		background: 'rgba(0, 165, 255, 0.05)',
		border: '#00A5FF',
		text: '#00A5FF',
	}
}

const buildChipSx = (colors: { background: string; border: string; text: string }) => ({
	height: '24px',
	borderRadius: '999px',
	border: `1px solid ${colors.border}`,
	backgroundColor: colors.background,
	color: colors.text,
	fontWeight: 500,
	fontSize: '12px',
	fontFamily: 'Poppins, sans-serif',
	'& .MuiChip-label': {
		padding: '0 6px 0 4px',
		lineHeight: '18px',
		whiteSpace: 'nowrap',
	},
	'& .MuiChip-icon': {
		color: colors.text,
		fontSize: '16px',
		marginLeft: '4px',
		marginRight: '2px',
	},
})

const buildPillIconButtonSx = (colors: { background: string; border: string; text: string }) => ({
	padding: '5px',
	borderRadius: '999px',
	color: colors.text,
	'&:hover': {
		backgroundColor: colors.background,
	},
})

const menuPaperSx = {
	borderRadius: '6px',
	boxShadow:
		'0 3px 14px 2px rgba(9, 32, 86, 0.12), 0 8px 10px 1px rgba(9, 32, 86, 0.14), 0 5px 5px -3px rgba(9, 32, 86, 0.2)',
	'& .MuiMenuItem-root': {
		fontSize: '12px',
		fontWeight: 500,
		fontFamily: 'Poppins, sans-serif',
	},
}

const selectMenuProps = {
	PaperProps: {
		sx: menuPaperSx,
	},
}

const formatDate = (value: Date) => {
	const day = String(value.getDate()).padStart(2, '0')
	const month = String(value.getMonth() + 1).padStart(2, '0')
	const year = value.getFullYear()
	return `${day}/${month}/${year}`
}

const buildDefaultCreateTask = (): CreateTaskForm => {
	const today = formatDate(new Date())
	return {
		startDate: today,
		endDate: today,
		destinatario: '',
		motorista: '',
		viatura: '',
		descricao: '',
		recorrencia: '',
		impedeEscalar: false,
	}
}

const destinatarioOptions = ['0001 - Ana Antunes', '0002 - Carla Silva']
const motoristaOptions = ['0109 - Agostinho Ferreira', '0137 - Gabriel Santos']
const viaturaOptions = ['0012 - Iveco 5T - I. Mota', '0008 - Sprinter 3T']
const recorrenciaOptions = ['Nunca', 'Diaria', 'Semanal', 'Mensal']
const impedeOptions = ['Nao', 'Sim'] as const
const estadoOptions = ['Nao Concluida', 'Concluida'] as const

const CustomModalScheduleTasks: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const tasksData = modalData as ScheduleTasksModalData | undefined
	const [handleSave] = modalHandlers ?? []

	const tasks = useMemo<ScheduleTasksModalData['tasks']>(() => {
		if (tasksData?.tasks?.length) {
			return tasksData.tasks
		}
		return defaultScheduleTasks
	}, [tasksData])

	const initialRows = useMemo<TaskRow[]>(() => {
		return tasks.map((task, index) => ({
			id: String(task.id ?? `row-${index}`),
			displayId: task.code ?? String(task.id ?? ''),
			startDate: task.startDate ?? '02/05/2024 - 08:00',
			endDate: task.endDate ?? '03/05/2024 - 08:00',
			impedeEscalar: task.impedeEscalar ? 'Sim' : 'Nao',
			estado: task.estado ?? (task.status === 'completed' ? 'Concluida' : 'Nao Concluida'),
			descricao: task.descricao ?? task.description ?? task.title ?? '',
			nome: task.nome ?? '',
			nr: task.nr ?? '',
			nrMec: task.nrMec ?? task.nrM ?? task.nr ?? '',
			nrFrota: task.nrFrota ?? task.nr ?? '',
			matricula: task.matricula ?? task.nr ?? '',
		}))
	}, [tasks])

	const [rows, setRows] = useState<TaskRow[]>(initialRows)
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
	const [startDate, setStartDate] = useState<Dayjs | null>(dayjs('01/05/2024', 'DD/MM/YYYY'))
	const [endDate, setEndDate] = useState<Dayjs | null>(dayjs('01/06/2024', 'DD/MM/YYYY'))
	const [onlyNotCompleted, setOnlyNotCompleted] = useState(false)
	const [viewMode, setViewMode] = useState<'table' | 'list'>('list')
	const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false)
	const [createTask, setCreateTask] = useState<CreateTaskForm>(() => buildDefaultCreateTask())
	const [pillMenuAnchor, setPillMenuAnchor] = useState<null | HTMLElement>(null)
	const [pillMenuContext, setPillMenuContext] = useState<PillMenuContext | null>(null)

	useEffect(() => {
		setRows(initialRows)
		setSelectedRowIds([])
	}, [initialRows])

	const filteredRows = useMemo(() => {
		if (!onlyNotCompleted) {
			return rows
		}
		return rows.filter(row => row.estado === 'Nao Concluida')
	}, [onlyNotCompleted, rows])

	const handleClearFilters = () => {
		setStartDate(dayjs('01/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('01/06/2024', 'DD/MM/YYYY'))
		setOnlyNotCompleted(false)
	}

	const handleSaveTasks = () => {
		if (typeof handleSave === 'function') {
			handleSave(rows)
		}
		closeModal()
	}

	const handleChangeImpede = (rowId: string, value: TaskRow['impedeEscalar']) => {
		setRows(prev => prev.map(row => (row.id === rowId ? { ...row, impedeEscalar: value } : row)))
	}

	const handleChangeEstado = (rowId: string, value: TaskRow['estado']) => {
		setRows(prev => prev.map(row => (row.id === rowId ? { ...row, estado: value } : row)))
	}

	const handleOpenPillMenu = (event: React.MouseEvent<HTMLElement>, rowId: string, field: PillMenuContext['field']) => {
		setPillMenuAnchor(event.currentTarget)
		setPillMenuContext({ rowId, field })
	}

	const handleClosePillMenu = () => {
		setPillMenuAnchor(null)
		setPillMenuContext(null)
	}

	const handleSelectPillValue = (value: TaskRow['impedeEscalar'] | TaskRow['estado']) => {
		if (!pillMenuContext) {
			return
		}
		if (pillMenuContext.field === 'impedeEscalar') {
			handleChangeImpede(pillMenuContext.rowId, value as TaskRow['impedeEscalar'])
		} else {
			handleChangeEstado(pillMenuContext.rowId, value as TaskRow['estado'])
		}
		handleClosePillMenu()
	}

	const rowHeight = 32
	const gridHeight = Math.max(Math.min(240, filteredRows.length * rowHeight) + 40, 160)

	const gridColumns = useMemo<GridColDef<TaskRow>[]>(
		() =>
			tableColumns.map(column => {
				const width = Number.parseInt(column.width, 10)
				const baseColumn = {
					field: column.key,
					headerName: column.label,
					width,
				}

				if (column.key === 'impedeEscalar') {
					return {
						...baseColumn,
						renderCell: (params: { row: TaskRow }) => {
							const colors = getImpedeColors(params.row.impedeEscalar)
							const ImpedeIcon = params.row.impedeEscalar === 'Sim' ? ReportProblemOutlinedIcon : CheckRoundedIcon
							return (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
								>
									<Chip
										clickable
										size='small'
										icon={<ImpedeIcon sx={{ fontSize: 16 }} />}
										label={params.row.impedeEscalar}
										variant='outlined'
										onClick={event => handleOpenPillMenu(event, params.row.id, 'impedeEscalar')}
										sx={buildChipSx(colors)}
									/>
									<IconButton
										size='small'
										onClick={event => handleOpenPillMenu(event, params.row.id, 'impedeEscalar')}
										sx={buildPillIconButtonSx(colors)}
									>
										<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Box>
							)
						},
					} as GridColDef<TaskRow>
				}

				if (column.key === 'estado') {
					return {
						...baseColumn,
						renderCell: (params: { row: TaskRow }) => {
							const colors = getEstadoColors(params.row.estado)
							const EstadoIcon = params.row.estado === 'Concluida' ? CheckRoundedIcon : InfoOutlinedIcon
							return (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
								>
									<Chip
										clickable
										size='small'
										icon={<EstadoIcon sx={{ fontSize: 16 }} />}
										label={params.row.estado}
										variant='outlined'
										onClick={event => handleOpenPillMenu(event, params.row.id, 'estado')}
										sx={buildChipSx(colors)}
									/>
									<IconButton
										size='small'
										onClick={event => handleOpenPillMenu(event, params.row.id, 'estado')}
										sx={buildPillIconButtonSx(colors)}
									>
										<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Box>
							)
						},
					} as GridColDef<TaskRow>
				}

				if (column.key === 'descricao') {
					return {
						...baseColumn,
						renderCell: (params: { value: string }) => (
							<Tooltip title={params.value ?? ''}>
								<Typography
									sx={{
										fontSize: '12px',
										color: '#092056',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										lineHeight: '1.3',
										fontFamily: 'Poppins, sans-serif',
									}}
								>
									{params.value ?? ''}
								</Typography>
							</Tooltip>
						),
					} as GridColDef<TaskRow>
				}

				return {
					...baseColumn,
					renderCell: (params: { value: string }) => {
						const isEmphasized = column.key === 'nrFrota' || column.key === 'matricula'
						return (
							<Typography
								sx={{
									fontSize: '12px',
									color: '#092056',
									lineHeight: isEmphasized ? '24px' : '1.3',
									fontFamily: 'Poppins, sans-serif',
									fontWeight: isEmphasized ? 500 : 400,
									letterSpacing: isEmphasized ? '0.15px' : '0',
								}}
							>
								{params.value ?? ''}
							</Typography>
						)
					},
				} as GridColDef<TaskRow>
			}),
		[handleOpenPillMenu],
	)

	const handleCreateTaskFieldChange = (field: keyof CreateTaskForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target
		setCreateTask(prev => ({ ...prev, [field]: value }))
	}

	const handleCreateTaskSelectChange =
		(field: keyof CreateTaskForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target
			setCreateTask(prev => ({ ...prev, [field]: value }))
		}

	const handleCreateTaskCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCreateTask(prev => ({ ...prev, impedeEscalar: event.target.checked }))
	}

	const getNextDisplayId = () => {
		const numericIds = rows.map(row => Number(row.displayId)).filter(value => !Number.isNaN(value))
		const nextId = numericIds.length ? Math.max(...numericIds) + 1 : rows.length + 1
		return String(nextId)
	}

	const getPersonCode = (value: string) => value.split('-')[0]?.trim() || value
	const getPersonName = (value: string) => value.split('-')[1]?.trim() || value
	const formatDateTime = (value: string) => (value.includes(' - ') ? value : value.replace(' ', ' - '))

	const resetCreateTaskForm = () => {
		setCreateTask(buildDefaultCreateTask())
	}

	const handleCancelCreateTask = () => {
		resetCreateTaskForm()
		setIsCreateTaskOpen(false)
	}

	const handleAddTask = () => {
		const displayId = getNextDisplayId()
		const personCode = getPersonCode(createTask.destinatario)
		const newRow: TaskRow = {
			id: `task-${Date.now()}`,
			displayId,
			startDate: formatDateTime(createTask.startDate),
			endDate: formatDateTime(createTask.endDate),
			impedeEscalar: createTask.impedeEscalar ? 'Sim' : 'Nao',
			estado: 'Nao Concluida',
			descricao: createTask.descricao || '--',
			nome: getPersonName(createTask.destinatario),
			nr: personCode,
			nrMec: personCode,
			nrFrota: personCode,
			matricula: personCode,
		}

		setRows(prev => [newRow, ...prev])
		resetCreateTaskForm()
	}

	const handleOpenCreateTask = () => {
		setIsCreateTaskOpen(true)
	}

	if (!tasksData) {
		return null
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 100%)',
					borderRadius: '4px',
					backgroundColor: '#FFFFFF',
					boxShadow:
						'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px 8px' }}>
					<Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#092056' }}>Tarefas da Escala</Typography>
				</Box>
				<Box
					sx={{
						padding: '0 24px 16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '16px',
							flexWrap: 'wrap',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								label='Data Inicial'
								value={startDate}
								onChange={newValue => setStartDate(newValue)}
								format='DD/MM/YYYY HH:mm'
								slotProps={{
									textField: {
										size: 'small',
										sx: [outlinedFieldSx, calendarInputSx, { width: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
								ampm={false}
							/>
						</LocalizationProvider>
						<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>-</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								label='Data Final'
								value={endDate}
								onChange={newValue => setEndDate(newValue)}
								format='DD/MM/YYYY HH:mm'
								slotProps={{
									textField: {
										size: 'small',
										sx: [outlinedFieldSx, calendarInputSx, { width: '140px' }],
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
								ampm={false}
							/>
						</LocalizationProvider>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={onlyNotCompleted}
									onChange={event => setOnlyNotCompleted(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label={
								<Typography
									sx={{
										fontSize: '13px',
										fontWeight: 500,
										fontFamily: 'Poppins, sans-serif',
										color: '#092056',
									}}
								>
									Apenas tarefas Nao Concluidas
								</Typography>
							}
							sx={{ marginLeft: '6px' }}
						/>
						<Box sx={{ marginLeft: 'auto' }}>
							<Typography
								sx={{
									fontSize: '13px',
									fontWeight: 600,
									color: '#00A5FF',
									cursor: 'pointer',
								}}
								onClick={handleClearFilters}
							>
								Limpar Filtros
							</Typography>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '8px 24px 16px',
					}}
				>
					<Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#092056' }}>
						{filteredRows.length} Tarefas
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<Box
							component='button'
							type='button'
							aria-expanded={isCreateTaskOpen}
							aria-controls='create-task-panel'
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								color: '#00A5FF',
								background: 'none',
								border: 'none',
								padding: 0,
								cursor: 'pointer',
							}}
							onClick={handleOpenCreateTask}
						>
							<Typography
								sx={{
									fontSize: '14px',
									fontWeight: 600,
									color: 'inherit',
								}}
							>
								Criar Nova Tarefa
							</Typography>
							<AddRoundedIcon sx={{ fontSize: 16, color: '#00A5FF' }} />
						</Box>
						<ToggleButtonGroup
							size='small'
							exclusive
							value={viewMode}
							onChange={(_, nextValue) => {
								if (nextValue) {
									setViewMode(nextValue)
								}
							}}
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '4px',
								'& .MuiToggleButton-root': {
									border: 'none',
									borderRadius: 0,
									padding: '4.8px',
									color: '#00A5FF',
									minWidth: '24px',
								},
								'& .MuiToggleButton-root.Mui-selected': {
									backgroundColor: '#F5F7FC',
									color: '#00A5FF',
								},
								'& .MuiToggleButton-root:not(:last-of-type)': {
									borderRight: '1px solid rgba(134, 149, 175, 0.15)',
								},
							}}
						>
							<ToggleButton
								value='table'
								aria-label='table view'
							>
								<TableRowsIcon sx={{ fontSize: 14 }} />
							</ToggleButton>
							<ToggleButton
								value='list'
								aria-label='list view'
							>
								<ViewHeadlineIcon sx={{ fontSize: 14 }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				</Box>

				<Collapse
					in={isCreateTaskOpen}
					timeout={260}
					easing={{
						enter: 'cubic-bezier(0.2, 0, 0, 1)',
						exit: 'cubic-bezier(0.4, 0, 1, 1)',
					}}
					unmountOnExit
				>
					<Box
						id='create-task-panel'
						sx={{
							backgroundColor: 'rgba(245, 247, 252, 0.5)',
							borderRadius: '5px',
							padding: '16px',
							margin: '0 24px',
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
						}}
					>
						<Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#092056' }}>Criar Nova Tarefa</Typography>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '16px',
								alignItems: 'center',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
									flex: '1 1 378px',
									minWidth: '300px',
								}}
							>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										label='Data Inicial'
										value={createTask.startDate ? dayjs(createTask.startDate, 'DD/MM/YYYY HH:mm') : null}
										onChange={newValue =>
											handleCreateTaskFieldChange('startDate')({
												target: { value: newValue?.format('DD/MM/YYYY HH:mm') || '' },
											} as any)
										}
										format='DD/MM/YYYY HH:mm'
										slotProps={{
											textField: {
												size: 'small',
												sx: [outlinedFieldSx, calendarInputSx, { flex: '1 1 0', minWidth: '150px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
										ampm={false}
									/>
								</LocalizationProvider>
								<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>-</Typography>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DateTimePicker
										label='Data Final'
										value={createTask.endDate ? dayjs(createTask.endDate, 'DD/MM/YYYY HH:mm') : null}
										onChange={newValue =>
											handleCreateTaskFieldChange('endDate')({
												target: { value: newValue?.format('DD/MM/YYYY HH:mm') || '' },
											} as any)
										}
										format='DD/MM/YYYY HH:mm'
										slotProps={{
											textField: {
												size: 'small',
												sx: [outlinedFieldSx, calendarInputSx, { flex: '1 1 0', minWidth: '150px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
										ampm={false}
									/>
								</LocalizationProvider>
							</Box>
							<TextField
								select
								label='Utilizador Destinatario'
								size='small'
								value={createTask.destinatario}
								onChange={handleCreateTaskSelectChange('destinatario')}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
									MenuProps: selectMenuProps,
								}}
								sx={{ ...outlinedFieldSx, flex: '1 1 200px', minWidth: '200px' }}
							>
								<MenuItem value=''>&nbsp;</MenuItem>
								{destinatarioOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label='Motorista'
								size='small'
								value={createTask.motorista}
								onChange={handleCreateTaskSelectChange('motorista')}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
									MenuProps: selectMenuProps,
								}}
								sx={{ ...outlinedFieldSx, flex: '1 1 200px', minWidth: '200px' }}
							>
								<MenuItem value=''>&nbsp;</MenuItem>
								{motoristaOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								label='Viatura'
								size='small'
								value={createTask.viatura}
								onChange={handleCreateTaskSelectChange('viatura')}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
									MenuProps: selectMenuProps,
								}}
								sx={{ ...outlinedFieldSx, flex: '1 1 200px', minWidth: '200px' }}
							>
								<MenuItem value=''>&nbsp;</MenuItem>
								{viaturaOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '16px',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<TextField
								label='Descricao'
								size='small'
								value={createTask.descricao}
								onChange={handleCreateTaskFieldChange('descricao')}
								sx={{
									...outlinedFieldSx,
									flex: '1 1 872px',
									maxWidth: '872px',
									minWidth: '240px',
								}}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={createTask.impedeEscalar}
										onChange={handleCreateTaskCheckboxChange}
										sx={checkboxSx}
									/>
								}
								label={
									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 500,
											fontFamily: 'Poppins, sans-serif',
											color: '#092056',
										}}
									>
										Impede o Escalonamento
									</Typography>
								}
							/>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									gap: '16px',
								}}
							>
								<Typography
									sx={{
										fontSize: '14px',
										fontWeight: 600,
										fontFamily: 'Poppins, sans-serif',
										color: '#092056',
									}}
								>
									Recorrencia da Tarefa:
								</Typography>
								<TextField
									select
									label='Repetir Tarefa'
									size='small'
									value={createTask.recorrencia}
									onChange={handleCreateTaskSelectChange('recorrencia')}
									SelectProps={{
										IconComponent: KeyboardArrowDownIcon,
										MenuProps: selectMenuProps,
									}}
									sx={{ ...outlinedFieldSx, width: '200px' }}
								>
									<MenuItem value=''>&nbsp;</MenuItem>
									{recorrenciaOptions.map(option => (
										<MenuItem
											key={option}
											value={option}
										>
											{option}
										</MenuItem>
									))}
								</TextField>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
								<Typography
									sx={{
										fontSize: '14px',
										fontWeight: 600,
										color: '#00A5FF',
										cursor: 'pointer',
									}}
									onClick={handleCancelCreateTask}
								>
									Cancelar
								</Typography>
								<Typography
									sx={{
										fontSize: '14px',
										fontWeight: 600,
										color: '#00A5FF',
										cursor: 'pointer',
									}}
									onClick={handleAddTask}
								>
									Adicionar
								</Typography>
							</Box>
						</Box>
					</Box>
				</Collapse>

				<Box
					sx={{
						overflow: 'hidden',
						backgroundColor: '#FFFFFF',
						margin: '0 24px',
					}}
				>
					<Box sx={{ height: gridHeight }}>
						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={filteredRows}
							columns={gridColumns}
							density='compact'
							rowHeight={rowHeight}
							columnHeaderHeight={40}
							checkboxSelection
							rowSelectionModel={selectedRowIds}
							onRowSelectionModelChange={newSelection => {
								setSelectedRowIds(newSelection as string[])
							}}
							disableRowSelectionOnClick
							hideFooter
							sx={[tableSx, dataGridDensityTransitionSx]}
						/>
					</Box>
				</Box>

				<Menu
					anchorEl={pillMenuAnchor}
					open={Boolean(pillMenuAnchor)}
					onClose={handleClosePillMenu}
					PaperProps={{ sx: menuPaperSx }}
				>
					{(pillMenuContext?.field === 'estado' ? estadoOptions : impedeOptions).map(option => (
						<MenuItem
							key={option}
							onClick={() => handleSelectPillValue(option)}
						>
							{option}
						</MenuItem>
					))}
				</Menu>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '16px',
						padding: '8px 24px 16px',
						backgroundColor: '#FFFFFF',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handleSaveTasks}
						sx={popupActionButtonSx}
					>
						Guardar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalScheduleTasks
